import { useState, useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { useFetchArrayResource, RESOURCES } from 'api'
import { Button, P, Wrapper } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'
import { useSelector } from 'react-redux'

import { FAREWILL_LEGAL_SERVICES } from 'utils/enums'
import { findCaseBookedInvoice } from 'utils/helpers'
import useApi from 'lib/effects/api'

import MessageBox from 'components/message-box'
import { useTasksContext } from 'components/tasks-panel/context'

import Dinero from 'dinero.js'

import type { ProbateCase } from 'lib/models/probate-case'
import { Invoice } from 'lib/models/invoice'
import { formatDate } from 'lib/formatting/dates'
import { caseBookedInvoiceTotalPriceWithVat } from './helpers'
import { QuoteResponseDataProbate } from 'lib/types'
import { formatCurrency, hasDecimals } from 'lib/formatting/currency'

Dinero.globalLocale = 'en-GB'
Dinero.defaultCurrency = 'GBP'

const NewInvoiceForm = () => {
  const { fetchTasks } = useTasksContext()
  const probateCase = useSelector(
    (state: { probateCase: ProbateCase }) => state.probateCase
  )
  const { referredTo } = probateCase.attributes

  const [, makeRequest] = useApi()
  const [isBooking, setIsBooking] = useState(false)
  const cache = useQueryClient()

  const handleClick = async () => {
    setIsBooking(true)
    try {
      await makeRequest({
        url: `/api/probate-cases/${probateCase.id}/book`,
        method: 'POST',
      })

      cache.invalidateQueries([
        RESOURCES.INVOICES,
        `filter[probateCaseId]=${probateCase.id}`,
      ])
      cache.invalidateQueries([RESOURCES.PROBATE_QUOTE_HISTORY, probateCase.id])
      await fetchTasks({
        queryParams: {
          'filter[probateCaseId]': probateCase.id,
          'filter[isNeeded]': true,
          'page[size]': 200,
        },
      })
      window.location.reload()
    } finally {
      setIsBooking(false)
    }
  }

  return (
    <>
      {referredTo === FAREWILL_LEGAL_SERVICES && (
        <MessageBox margin={[0, 0, GTR.S]}>
          Booking the case with Dignity Legal Services creates a case in
          Exizent, and a 'Matter' in Clio.
        </MessageBox>
      )}

      <Button.Primary onClick={handleClick} loading={isBooking}>
        Book case
      </Button.Primary>
    </>
  )
}
NewInvoiceForm.displayName = 'NewInvoiceForm'

const getPriceQuoted = (
  estateAdminRequired: boolean,
  caseBookedInvoice: Invoice | undefined,
  estimatedExtrasInPence = 0,
  calculatedPrice = 0
) => {
  const IS_FEA_CASE = estateAdminRequired
  const GOP_CASE_BOOKED = !estateAdminRequired && caseBookedInvoice

  if (IS_FEA_CASE) {
    // FEA Calculated prices always include disbursements (and is given in pence)
    return calculatedPrice / 100
  }

  if (GOP_CASE_BOOKED) {
    // Use case helper to keep display consistent (gives output in pounds)
    return caseBookedInvoiceTotalPriceWithVat(caseBookedInvoice)
  }

  // When GOP case is not booked we have to manually add disbursements (both values are in pence)
  return (calculatedPrice + estimatedExtrasInPence) / 100
}

const Price = () => {
  const probateCase = useSelector(
    (state: { probateCase: ProbateCase }) => state.probateCase
  )
  const [{ data: quote }, makeRequest] = useApi<QuoteResponseDataProbate>()

  const { leadId } = probateCase.attributes

  useEffect(() => {
    if (leadId) {
      makeRequest({
        url: '/api/quotes',
        method: 'POST',
        data: {
          data: {
            type: 'quotes',
            attributes: {
              product: 'probate',
              leadId,
              showEstateAdminBreakdown: true,
            },
          },
        },
      }).catch(() => undefined)
    }
  }, [leadId, makeRequest])

  const { data: { data: invoices = [] } = {}, isLoading } =
    useFetchArrayResource<Invoice>(
      {
        resource: RESOURCES.INVOICES,
        params: `filter[probateCaseId]=${probateCase.id}`,
      },
      {
        enabled: !!probateCase.id,
      }
    )
  const caseBookedInvoice = findCaseBookedInvoice(invoices)

  if (isLoading) {
    return <p>Loading booking details…</p>
  }

  const estimatedExtrasInPence = quote
    ? quote.attributes.grantOnly.courtFeeInPence +
      quote.attributes.grantOnly.grantCopyPriceInPence *
        quote.attributes.grantOnly.grantCopyQuantity
    : 0

  const hasCalculatedPrice = Boolean(probateCase.attributes.calculatedPrice)
  const calculatedPrice = getPriceQuoted(
    probateCase.attributes.estateAdministrationRequired,
    caseBookedInvoice,
    estimatedExtrasInPence,
    probateCase.attributes.calculatedPrice || 0
  )

  return (
    <Wrapper>
      <P margin={[0, 0, 'XS']}>
        Price quoted:{' '}
        {hasCalculatedPrice ? (
          <>
            <strong>
              {formatCurrency({
                value: calculatedPrice,
                showPence: hasDecimals(calculatedPrice),
              })}
            </strong>{' '}
            (inc VAT)
          </>
        ) : (
          <strong>No quote given on initial call</strong>
        )}
      </P>
      {caseBookedInvoice ? (
        <P>
          Case booked:{' '}
          <strong>{formatDate(caseBookedInvoice.attributes.createdAt)}</strong>
        </P>
      ) : (
        <Wrapper margin={['S', 0, 0]}>
          <NewInvoiceForm />
        </Wrapper>
      )}
    </Wrapper>
  )
}
Price.displayName = 'Price'

export default Price
